import { useEffect, useState } from "react";
import * as yup from "yup";
import Loader from "../../../../components/Loader";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import useNavigationGuard from "../../../../hooks/useNavigationGuard";
import { useLocation, useNavigate } from "react-router-dom";
import { CreateHandleUnsaveNavigation } from "../../../../utilities/handleUnsaveNavigation";
import { HandleUnsaveNavigationWithFun } from "../../../../utilities/handleUnsaveNavigationWithFun";
import { Box, Grid2, TextField } from "@mui/material";
import style from "../Subject.module.css";
import {
  formatDateWithoutTime,
  notifyError,
  notifySuccess,
  validDateRegex,
} from "../../../../utilities/helper";
import CustomSwitch from "../../../../components/CustomSwitch/CustomSwitch";
import {
  getOnLoadDataServiceForUnblindSubject,
  postUnblindSubject,
} from "../../../../services/subjectService";
import UnblindConfirmationPage from "./UnblindConfirmationPage/UnblindConfirmationPage";

const UnblindSubject = () => {
  const [load, setLoad] = useState(false);
  const [subjectDetails, setSubjectDetails] = useState({
    subjectName: "",
    subjectNumber: "",
    subjectStatus: "",
    attributes: [],
    payload: null,
  });
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmationData, setConfirmationData] = useState(null);

  const unblindSubjectSchema = yup.object().shape({
    unblindSubject: yup.boolean(),
  });

  const navigate = useNavigate();

  const access_token = localStorage?.getItem("access_token");
  const id = useLocation()?.state?.id;
  const role = localStorage.getItem("role");
  const cultureCode = localStorage.getItem("currentUserCultureCode");
  const roleId = localStorage.getItem("roleId");

  const { subjectName, subjectNumber, subjectStatus, attributes, payload } =
    subjectDetails;

  const handleUnsaveNavigation = CreateHandleUnsaveNavigation();
  const handleUnSaveWithFun = HandleUnsaveNavigationWithFun();

  const {
    handleSubmit,
    control,
    watch,
    formState: { isDirty },
  } = useForm({
    resolver: yupResolver(unblindSubjectSchema),
    defaultValues: {
      unblindSubject: false,
    },
  });

  useNavigationGuard(isDirty);

  const { unblindSubject } = watch();

  useEffect(() => {
    if (access_token && id && roleId && cultureCode) {
      getOnLoadDataService(id);
    }
  }, [roleId, id, cultureCode]);

  const getOnLoadDataService = async (id) => {
    try {
      setLoad(true);
      const res = await getOnLoadDataServiceForUnblindSubject(
        access_token,
        id,
        cultureCode,
        roleId,
      );
      console.log("Res", res);
      if (res?.data?.message === "Onload data extracted for Unblind page!") {
        const data = res?.data?.result?.patientData;

        setSubjectDetails({
          subjectNumber: data?.patientNumber || "",
          subjectName: data?.patientNumber || "",
          attributes: data?.attributes || [],
          subjectStatus: data?.patientStatus,
          payload: data,
        });
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoad(false);
    }
  };

  const onSubmit = async () => {
    try {
      setLoad(true);
      const res = await postUnblindSubject(
        access_token,
        roleId,
        cultureCode,
        payload,
      );
      if (
        res?.data?.status === "OK" &&
        res?.data?.message === "Patient unblinded successfully!"
      ) {
        setConfirmationData(res?.data?.result);
        setShowConfirm(true);
        notifySuccess(res?.data?.message);
      }
    } catch (err) {
      console.log("Error: ", err?.message);
      setShowConfirm(false);
      notifyError(err?.message || "Something Went Wrong ");
      setConfirmationData(null);
      if (err?.status == 401) {
        navigate("/unAuthorize");
      }
    } finally {
      setLoad(false);
    }
  };

  return (
    <>
      {showConfirm ? (
        <UnblindConfirmationPage
          data={confirmationData}
          id={id}
          setShowConfirm={setShowConfirm}
          subjectName={subjectName}
        ></UnblindConfirmationPage>
      ) : (
        <>
          {load ? (
            <Loader />
          ) : (
            <div className="content-body">
              <p className="bread-crumbs-link-container">
                <button
                  className="bread-crumbs-btn"
                  onClick={(e) =>
                    handleUnsaveNavigation(e, isDirty, "", navigate)
                  }
                >
                  Home
                </button>
                {" | "}
                <button
                  className="bread-crumbs-btn"
                  onClick={(e) => {
                    handleUnSaveWithFun(
                      e,
                      isDirty,
                      () => navigate("/subject"),
                      () => {},
                    );
                  }}
                >
                  Subject Management
                </button>{" "}
                |{" "}
                <button
                  className="bread-crumbs-btn"
                  onClick={(e) => {
                    handleUnSaveWithFun(
                      e,
                      isDirty,
                      () =>
                        navigate("/update-subject", {
                          state: {
                            id: id,
                          },
                        }),
                      () => {},
                    );
                  }}
                >
                  {subjectName}
                </button>{" "}
                |{" "}
                <button
                  className="bread-crumbs-btn"
                  to="/subject-randomization-treatment"
                >
                  Unblind Subject
                </button>
                -{" "}
                <b style={{ color: "gray" }}>
                  {localStorage?.getItem("studyName")}
                </b>
              </p>

              <Box
                component="form"
                autoComplete="off"
                sx={{ height: "auto", width: "100%" }}
                onSubmit={handleSubmit(onSubmit)}
              >
                <Grid2
                  container
                  alignItems="start"
                  flexDirection={"column"}
                  justifyContent={"space-between"}
                  sx={{ width: "100%", margin: "0 auto" }}
                  gap={1}
                >
                  <Grid2 size={{ xl: 8, md: 8, lg: 8, sm: 12, xs: 12 }}>
                    <div>
                      <p className="labelStyles">Subject Number</p>
                      <TextField
                        fullWidth
                        size="small"
                        disabled={true}
                        type={"text"}
                        value={subjectNumber}
                      />
                    </div>
                  </Grid2>

                  <Grid2 size={{ xl: 8, md: 8, lg: 8, sm: 12, xs: 12 }}>
                    <div>
                      <p className="labelStyles">Subject Status</p>
                      <TextField
                        fullWidth
                        size="small"
                        disabled={true}
                        type={"text"}
                        value={subjectStatus}
                      />
                    </div>
                  </Grid2>
                  <Grid2 size={{ xl: 12, md: 12, lg: 12, sm: 12, xs: 12 }}>
                    {attributes && attributes.length > 0 && (
                      <div
                        className={style?.subjectDetailMainContainer}
                        style={{
                          margin: "20px 0px",
                        }}
                      >
                        {attributes?.map((item) => (
                          <div
                            className={style?.subjectDetailOuterContainer}
                            key={item?.keyName}
                          >
                            <span className={style?.subjectDetailName}>
                              {item?.label}
                            </span>
                            <span className={style?.subjectDetailValue}>
                              {validDateRegex.test(item?.value)
                                ? formatDateWithoutTime(item?.value)
                                : item?.value}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </Grid2>
                  <Grid2 size={{ xl: 8, md: 8, lg: 8, sm: 12, xs: 12 }}>
                    <div
                      style={{
                        display: "flex",
                        gap: "20px",
                        alignItems: "center",
                      }}
                    >
                      <p>
                        Are you sure you would like to Unblind this Subject's
                        treatment?
                      </p>
                      <Controller
                        name="unblindSubject"
                        control={control}
                        defaultValue={false}
                        render={({ field }) => (
                          <CustomSwitch
                            isSwitch={field.value}
                            setSwitch={(val) => field.onChange(val)}
                          />
                        )}
                      />
                    </div>
                  </Grid2>
                </Grid2>
                <Grid2
                  alignItems={"end"}
                  justifyContent="end"
                  container
                  gap={3}
                  sx={{ marginTop: "2%" }}
                >
                  <Grid2 item md={6}>
                    <button
                      type="button"
                      className="create-btn-outlined"
                      onClick={(e) => {
                        e.preventDefault();
                        handleUnSaveWithFun(
                          e,
                          isDirty,
                          () =>
                            navigate("/update-subject", {
                              state: {
                                id: id,
                              },
                            }),
                          () => {},
                        );
                      }}
                    >
                      Cancel
                    </button>
                  </Grid2>
                  <Grid2 item md={6}>
                    <button
                      type="submit"
                      className={
                        unblindSubject === false ||
                        subjectStatus === "Discontinued"
                          ? "disabled-btn"
                          : `create-btn`
                      }
                      disabled={
                        unblindSubject === false ||
                        subjectStatus === "Discontinued"
                          ? true
                          : false
                      }
                    >
                      Unblind Subject
                    </button>
                  </Grid2>
                </Grid2>
              </Box>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default UnblindSubject;
